import { useTranslation } from "@redotech/redo-i18n";
import classNames from "classnames";
import type { PointerEvent, TouchEvent } from "react";
import { memo } from "react";
import * as redoTableHeaderCellsCss from "./redo-table-header-cells.module.css";

export const MINIMUM_COLUMN_WIDTH = 45;

export function getResizedColumnWidth({
  currentWidth,
  widthDif,
}: {
  currentWidth: number;
  widthDif: number;
}): number {
  return Math.max(MINIMUM_COLUMN_WIDTH, currentWidth + widthDif);
}

export interface RedoTableColumnResizerProps {
  "data-dd-action-name"?: string;
  held: boolean;
  onAdjustColumnWidth: ({ widthDif }: { widthDif: number }) => void;
  onResizeStart?: ({ width }: { width: number }) => void;
  setHeld: (held: boolean) => void;
  showSeparator?: boolean;
}

export const RedoTableColumnResizer = memo(function RedoTableColumnResizer({
  held,
  onAdjustColumnWidth,
  onResizeStart,
  setHeld,
  showSeparator = false,
  "data-dd-action-name": actionName,
}: RedoTableColumnResizerProps) {
  const { t } = useTranslation();
  const handler = (pointerDownEvent: PointerEvent<HTMLDivElement>) => {
    if (!pointerDownEvent.isPrimary || pointerDownEvent.button !== 0) {
      return;
    }
    pointerDownEvent.preventDefault();
    pointerDownEvent.stopPropagation();

    const resizeHandle = pointerDownEvent.currentTarget;
    resizeHandle.setPointerCapture(pointerDownEvent.pointerId);
    const separatorRect = resizeHandle.getBoundingClientRect();
    const columnRect = resizeHandle.parentElement?.getBoundingClientRect();
    const initialOffsetToSeparator =
      pointerDownEvent.clientX - (separatorRect.left + separatorRect.width / 2);
    const startX = pointerDownEvent.pageX - initialOffsetToSeparator;
    const pointerId = pointerDownEvent.pointerId;
    const previousBodyCursor = document.body.style.cursor;
    const previousBodyUserSelect = document.body.style.userSelect;
    let rightColumnExpandInterval: ReturnType<typeof setInterval> | null = null;
    let currentWidthDif = 0;
    let mouseDisplacementX = 0;
    let didResize = false;

    onResizeStart?.({ width: columnRect?.width ?? 0 });
    setHeld(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    function startRightColumnExpandInterval() {
      if (rightColumnExpandInterval) {
        return;
      }
      rightColumnExpandInterval = setInterval(() => {
        currentWidthDif += 5;
        didResize = true;
        onAdjustColumnWidth({ widthDif: mouseDisplacementX + currentWidthDif });
      }, 25);
    }

    function clearRightColumnExpandInterval() {
      if (rightColumnExpandInterval) {
        clearInterval(rightColumnExpandInterval);
        rightColumnExpandInterval = null;
      }
    }

    function onPointerMove(pointerMoveEvent: globalThis.PointerEvent) {
      if (pointerMoveEvent.pointerId !== pointerId) {
        return;
      }
      const screenWidth = window.innerWidth;
      const distanceFromRightEdge = screenWidth - pointerMoveEvent.pageX;
      if (distanceFromRightEdge < 10) {
        startRightColumnExpandInterval();
      } else {
        clearRightColumnExpandInterval();
        mouseDisplacementX =
          pointerMoveEvent.pageX - initialOffsetToSeparator - startX;
        didResize = true;
        onAdjustColumnWidth({ widthDif: mouseDisplacementX + currentWidthDif });
      }
    }

    function preventClick(clickEvent: globalThis.MouseEvent) {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
    }

    function cleanup() {
      clearRightColumnExpandInterval();
      setHeld(false);
      document.body.style.cursor = previousBodyCursor;
      document.body.style.userSelect = previousBodyUserSelect;
      if (resizeHandle.hasPointerCapture(pointerId)) {
        resizeHandle.releasePointerCapture(pointerId);
      }
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerCancel);
      document.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("blur", cleanup);
    }

    function onPointerUp(pointerUpEvent: globalThis.PointerEvent) {
      if (pointerUpEvent.pointerId !== pointerId) {
        return;
      }
      cleanup();
      if (didResize) {
        document.addEventListener("click", preventClick, {
          capture: true,
          once: true,
        });
        window.setTimeout(() => {
          document.removeEventListener("click", preventClick, true);
        }, 0);
      }
    }

    function onPointerCancel(pointerCancelEvent: globalThis.PointerEvent) {
      if (pointerCancelEvent.pointerId === pointerId) {
        cleanup();
      }
    }

    function onMouseLeave() {
      cleanup();
    }

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerCancel);
    document.addEventListener("mouseleave", onMouseLeave, { once: true });
    window.addEventListener("blur", cleanup, { once: true });
  };

  const preventTouchReorder = (touchStartEvent: TouchEvent<HTMLDivElement>) => {
    touchStartEvent.stopPropagation();
  };

  return (
    <div
      aria-label={t("Resize column")}
      aria-orientation="vertical"
      className={classNames(
        redoTableHeaderCellsCss.columnResizer,
        held && redoTableHeaderCellsCss.columnResizerActive,
        showSeparator && redoTableHeaderCellsCss.columnResizerVisible,
      )}
      data-dd-action-name={actionName}
      data-table-resize-handle="true"
      data-table-resize-separator-visible={showSeparator ? "true" : undefined}
      data-table2-resize-handle="true"
      onPointerDown={handler}
      onTouchStart={preventTouchReorder}
      role="separator"
    />
  );
});
